import { Injectable } from "@angular/core";
import { Store } from "@ngrx/store";
import { getKey } from "pax-ui/core";
import { concatMap, filter, from, tap } from "rxjs";
import { InvalidOperationException } from "src/app/shared/exceptions/exceptions";
import { AppState } from "src/app/shared/store/app.state";
import { NotificationMessage } from "src/app/shared/store/notification/notification.state";
import * as NotificationActions from "../../../store/notification/notification.actions";
import * as NotificationSelectors from "../../../store/notification/notification.selectors";
import { BackgroundSubscriptionNavigationService } from "../../background-subscription-navigation.service";

@Injectable({
  providedIn: "root",
})
export class NotificationPublisherService {
  private readonly _timeBasedNotificationMessages = new Map<
    NotificationMessageId,
    TimeoutId
  >();

  constructor(
    private readonly _store: Store<AppState>,
    private readonly _backgroundSubscriptionNavigationService: BackgroundSubscriptionNavigationService,
  ) {}

  start() {
    this.startListeningToNavigationStartEvents();
    this.startListeningToNotificationMessages();
  }

  private startListeningToNavigationStartEvents() {
    this._backgroundSubscriptionNavigationService.hasNavigationStarted$
      .pipe(
        tap(() => {
          this._store.dispatch(
            NotificationActions.tryDeleteDeleteAfterNextNavigationNotificationMessages(),
          );
        }),
      )
      .subscribe();
  }

  private startListeningToNotificationMessages() {
    this._store
      .select(NotificationSelectors.selectNotificationMessages)
      .pipe(
        concatMap((notificationMessages) => from(notificationMessages ?? [])),
        filter((notificationMessage) =>
          this.isTimeBasedNotificationMessageWithNoTimer(notificationMessage),
        ),
        tap((notificationMessage) => {
          if (
            notificationMessage.deleteAfterNumberOfMilliseconds === undefined
          ) {
            throw new InvalidOperationException(
              `It should not be possible that the property '${getKey<NotificationMessage>(
                "deleteAfterNumberOfMilliseconds",
              )} can be 'undefined'. There is an explicit check in the filter clause which should prevent that`,
            );
          }

          const timeoutId =
            this.createDeleteNotificationMessageCallbackFunction(
              notificationMessage.id,
              notificationMessage.deleteAfterNumberOfMilliseconds,
            );

          this._timeBasedNotificationMessages.set(
            notificationMessage.id,
            timeoutId,
          );
        }),
      )
      .subscribe();
  }

  private createDeleteNotificationMessageCallbackFunction(
    notificationMessageId: number,
    timeoutInMilliseconds: number,
  ) {
    return setTimeout(() => {
      // We have to be careful that we do not run into a cycle when dispatching this action
      // as we are subscribing to the same state to which we are writing data to.
      // This could be avoided if we would subscribe to the 'createNotifications' action but this makes
      // it more awkward to match the notifications as at this point we do not have an id yet.
      this._store.dispatch(
        NotificationActions.tryDeleteNotification({
          notificationId: notificationMessageId,
        }),
      );

      const timeoutId = this._timeBasedNotificationMessages.get(
        notificationMessageId,
      );

      this._timeBasedNotificationMessages.delete(notificationMessageId);

      if (!timeoutId) {
        throw new InvalidOperationException(
          `Could not find a timeout id with the notification id '${notificationMessageId.toString()}'`,
        );
      }

      clearTimeout(timeoutId);
    }, timeoutInMilliseconds);
  }

  private isTimeBasedNotificationMessageWithNoTimer(
    notificationMessage: NotificationMessage,
  ) {
    const isTimeBasedNotificationMessage =
      notificationMessage.deleteAfterNumberOfMilliseconds !== undefined;
    const hasTimer = this._timeBasedNotificationMessages.has(
      notificationMessage.id,
    );

    return isTimeBasedNotificationMessage && !hasTimer;
  }
}

type TimeoutId = number;
type NotificationMessageId = number;
